import { useState, useCallback, useMemo } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@mustauto/ui/web';
import { isValidStockQuantity, roundStockQuantity } from '@mustauto/utils';
import { useSuppliers } from '@/hooks/useSuppliers';
import { useInventory } from '@/hooks/useInventory';
import type { PurchaseOrder } from '@/hooks/usePurchaseOrders';
import { formatReais } from '@/lib/utils';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useFocusTrap } from '@/hooks/useFocusTrap';

interface POFormItem {
  key: string;
  inventoryItemId: string;
  itemName: string;
  partNumber: string;
  quantityOrdered: string;
  unitPrice: string;
  notes: string;
}

function newLine(): POFormItem {
  return {
    key: crypto.randomUUID(),
    inventoryItemId: '',
    itemName: '',
    partNumber: '',
    quantityOrdered: '1',
    unitPrice: '',
    notes: '',
  };
}

interface PurchaseOrderFormProps {
  order?: PurchaseOrder | null;
  preselectedItemId?: string | null;
  onNavigateToSuppliers?: () => void;
  onSubmit: (data: {
    supplierId: string;
    items: {
      inventoryItemId?: string | null;
      itemName: string;
      partNumber?: string | null;
      quantityOrdered: number;
      unitPrice: number;
      notes?: string | null;
    }[];
    expectedDelivery?: string | null;
    notes?: string | null;
  }) => Promise<void>;
  onClose: () => void;
  isSubmitting: boolean;
}

export function PurchaseOrderForm({ order, preselectedItemId, onNavigateToSuppliers, onSubmit, onClose, isSubmitting }: PurchaseOrderFormProps) {
  useEscapeKey(onClose);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const { suppliers, isError: suppliersError, refetch: refetchSuppliers } = useSuppliers();
  const { items: inventoryItems } = useInventory();
  const isEditing = !!order;

  const [supplierId, setSupplierId] = useState(order?.supplierId ?? '');
  const [expectedDelivery, setExpectedDelivery] = useState(order?.expectedDelivery?.split('T')[0] ?? '');
  const [notes, setNotes] = useState(order?.notes ?? '');

  const initialLines = useMemo(() => {
    if (order?.items) {
      return order.items.map((item) => ({
        key: item.id,
        inventoryItemId: item.inventoryItemId ?? '',
        itemName: item.itemName,
        partNumber: item.partNumber ?? '',
        quantityOrdered: String(item.quantityOrdered),
        unitPrice: String(item.unitPrice),
        notes: item.notes ?? '',
      }));
    }
    if (preselectedItemId) {
      const invItem = inventoryItems.find((i) => i.id === preselectedItemId);
      if (invItem) {
        return [{
          key: crypto.randomUUID(),
          inventoryItemId: invItem.id,
          itemName: invItem.name,
          partNumber: invItem.partNumber ?? '',
          quantityOrdered: String(Math.max(roundStockQuantity(invItem.minQuantity * 2 - invItem.quantity), 1)),
          // Sem `estoque.custo` o custo do item vem nulo: a linha começa vazia.
          unitPrice: String(invItem.lastPurchasePrice ?? invItem.unitCost ?? ''),
          notes: '',
        }];
      }
    }
    return [newLine()];
  }, [order, preselectedItemId, inventoryItems]);

  const [lines, setLines] = useState<POFormItem[]>(initialLines);

  const updateLine = useCallback((key: string, field: keyof POFormItem, value: string) => {
    setLines((prev) => prev.map((l) => l.key === key ? { ...l, [field]: value } : l));
  }, []);

  const selectInventoryItem = useCallback((key: string, itemId: string) => {
    const invItem = inventoryItems.find((i) => i.id === itemId);
    if (!invItem) return;
    setLines((prev) => prev.map((l) =>
      l.key === key
        ? {
            ...l,
            inventoryItemId: invItem.id,
            itemName: invItem.name,
            partNumber: invItem.partNumber ?? '',
            unitPrice: String(invItem.lastPurchasePrice ?? invItem.unitCost ?? ''),
          }
        : l
    ));
  }, [inventoryItems]);

  const removeLine = useCallback((key: string) => {
    setLines((prev) => prev.length > 1 ? prev.filter((l) => l.key !== key) : prev);
  }, []);

  const addLine = useCallback(() => {
    setLines((prev) => [...prev, newLine()]);
  }, []);

  const total = lines.reduce((sum, l) => sum + (Number(l.quantityOrdered) || 0) * (Number(l.unitPrice) || 0), 0);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      await onSubmit({
        supplierId,
        items: lines.map((l) => ({
          inventoryItemId: l.inventoryItemId || null,
          itemName: l.itemName,
          partNumber: l.partNumber || null,
          quantityOrdered: Number(l.quantityOrdered),
          unitPrice: Number(l.unitPrice) || 0,
          notes: l.notes || null,
        })),
        expectedDelivery: expectedDelivery || null,
        notes: notes || null,
      });
    },
    [supplierId, lines, expectedDelivery, notes, onSubmit],
  );

  const canSubmit = supplierId && lines.every((l) => l.itemName.trim() && isValidStockQuantity(Number(l.quantityOrdered)) && Number(l.unitPrice) >= 0);

  return (
    <div ref={trapRef} role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold">
              {isEditing ? `Editar OC #${order?.number}` : 'Nova Ordem de Compra'}
            </h3>
            <button onClick={onClose} aria-label="Fechar" className="rounded-md p-1 hover:bg-muted"><X size={18} /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Supplier + Delivery */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex flex-col gap-1 sm:col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Fornecedor *</label>
                <div className="flex gap-2">
                  <select
                    value={supplierId}
                    onChange={(e) => setSupplierId(e.target.value)}
                    required
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Selecione</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={onNavigateToSuppliers}
                    title="Cadastrar novo fornecedor"
                    className="rounded-md border px-3 py-2 text-xs font-medium hover:bg-muted transition-colors"
                  >
                    <Plus size={14} />
                  </button>
                </div>
                {/* Sem isto o select vazio parece "não há fornecedor cadastrado",
                    e a OC é montada sem o fornecedor que existe. */}
                {suppliersError && suppliers.length === 0 && (
                  <button
                    type="button"
                    onClick={() => refetchSuppliers()}
                    className="mt-1 text-left text-xs text-destructive underline-offset-2 hover:underline"
                  >
                    Não foi possível carregar os fornecedores. Tentar novamente.
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Previsão de entrega</label>
                <input
                  type="date"
                  value={expectedDelivery}
                  onChange={(e) => setExpectedDelivery(e.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* Items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-muted-foreground uppercase">Itens</label>
                <button type="button" onClick={addLine}
                  className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/40 transition-colors">
                  <Plus size={12} /> Adicionar item
                </button>
              </div>
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Item</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground w-28">Cód. peça</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-20">Qtd</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-28">Preço unit.</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-28">Total</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line) => {
                      const lineTotal = (Number(line.quantityOrdered) || 0) * (Number(line.unitPrice) || 0);
                      return (
                        <tr key={line.key} className="border-b last:border-0">
                          <td className="px-3 py-2">
                            <select
                              value={line.inventoryItemId}
                              onChange={(e) => {
                                if (e.target.value) {
                                  selectInventoryItem(line.key, e.target.value);
                                } else {
                                  updateLine(line.key, 'inventoryItemId', '');
                                  updateLine(line.key, 'itemName', '');
                                }
                              }}
                              className="w-full rounded border border-input bg-background px-2 py-1 text-sm"
                            >
                              <option value="">Item avulso...</option>
                              {inventoryItems.map((item) => (
                                <option key={item.id} value={item.id}>{item.name} ({item.sku ?? 'sem SKU'})</option>
                              ))}
                            </select>
                            {!line.inventoryItemId && (
                              <input
                                type="text"
                                value={line.itemName}
                                onChange={(e) => updateLine(line.key, 'itemName', e.target.value)}
                                placeholder="Nome do item"
                                className="mt-1 w-full rounded border border-input bg-background px-2 py-1 text-sm"
                              />
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={line.partNumber}
                              onChange={(e) => updateLine(line.key, 'partNumber', e.target.value)}
                              className="w-full rounded border border-input bg-background px-2 py-1 text-sm"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={line.quantityOrdered}
                              onChange={(e) => updateLine(line.key, 'quantityOrdered', e.target.value)}
                              // Fracionado (2,5 L de óleo): o passo padrão do navegador é 1.
                              min={0.001}
                              step="0.001"
                              inputMode="decimal"
                              className="w-full rounded border border-input bg-background px-2 py-1 text-sm text-right"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={line.unitPrice}
                              onChange={(e) => updateLine(line.key, 'unitPrice', e.target.value)}
                              min={0}
                              step="0.01"
                              className="w-full rounded border border-input bg-background px-2 py-1 text-sm text-right"
                            />
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-sm">
                            {formatReais(lineTotal)}
                          </td>
                          <td className="px-3 py-2">
                            <button type="button" onClick={() => removeLine(line.key)}
                              aria-label="Remover linha"
                              className="rounded p-1 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/50">
                      <td colSpan={4} className="px-3 py-2 text-right text-sm font-semibold">Total:</td>
                      <td className="px-3 py-2 text-right font-mono font-bold">{formatReais(total)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Notes */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Observações</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={1000}
                rows={2}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={isSubmitting || !canSubmit}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Salvando...' : isEditing ? 'Salvar alterações' : 'Salvar rascunho'}
              </button>
              <button type="button" onClick={onClose}
                className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Cancelar
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
